<template>
  <section class="container-fluid px-0">
    <div class="px-0">
      <ul class="nav nav-tabs product-tabs mb-4">
        <li class="nav-item">
          <button type="button" class="nav-link" :class="{ active: activeTab === 'catalog' }" @click="setTab('catalog')">
            <i class="fas fa-globe me-2"></i>Catálogo web
          </button>
        </li>
        <li class="nav-item">
          <button type="button" class="nav-link" :class="{ active: activeTab === 'store' }" @click="setTab('store')">
            <i class="fas fa-store me-2"></i>Tienda ingreso
          </button>
        </li>
      </ul>

      <template v-if="activeTab === 'catalog'">
        <div class="panel-toolbar">
          <p class="panel-toolbar-meta">
            <i class="fas fa-seedling me-1 text-primary"></i>
            {{ totalProducts }} productos en kameseeds.com
          </p>
          <div class="panel-toolbar-actions d-flex flex-wrap gap-2 align-items-center">
            <ProductViewToggle v-model="viewMode" class="d-none d-md-inline-flex" />
            <button class="btn btn-outline-primary" @click="forceRefresh(true)" :disabled="isLoading || isRefreshing">
              <i class="fas" :class="isLoading || isRefreshing ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
              {{ isLoading || isRefreshing ? 'Actualizando...' : 'Actualizar' }}
            </button>
            <router-link to="/panel/productos/nuevo" class="btn btn-primary">
              <i class="fas fa-plus me-2"></i>
              Agregar producto
            </router-link>
          </div>
        </div>

        <div class="alert alert-secondary text-white-50 panel-card mb-4 mx-0">
          <i class="fas fa-info-circle me-2"></i>
          Fichas del catálogo visible en la web. Precios y ventas se gestionan en la pestaña
          <button type="button" class="btn btn-link text-white-50 alert-link p-0 align-baseline" @click="setTab('store')">
            Tienda ingreso
          </button>.
        </div>

        <div class="row mb-4">
          <div class="col-12">
            <div class="d-flex flex-wrap gap-3 align-items-center">
              <div class="flex-grow-1">
                <div class="input-group">
                  <span class="input-group-text bg-white border-end-0 text-muted">
                    <i class="fas fa-search"></i>
                  </span>
                  <input
                    type="text"
                    class="form-control border-start-0 ps-0 text-black"
                    v-model="filters.search"
                    placeholder="Buscar por nombre o marca..."
                  />
                </div>
              </div>

              <select class="form-select" v-model="filters.type" style="max-width: 200px;">
                <option value="">Todos los tipos</option>
                <option value="auto">Autoflorecientes</option>
                <option value="fem">Fotoperiódicas</option>
                <option value="regular">Regulares</option>
                <option value="merch">Merchandising</option>
              </select>

              <select class="form-select" v-model="filters.availability" style="max-width: 200px;">
                <option value="">Todas las disponibilidades</option>
                <option value="available">Disponibles</option>
                <option value="unavailable">No disponibles</option>
              </select>

              <select class="form-select" v-model="filters.edition" style="max-width: 200px;">
                <option value="">Todas las ediciones</option>
                <option value="limited">Edición limitada</option>
                <option value="regular">Edición regular</option>
              </select>

              <button class="btn btn-primary" @click="applyFilters" :disabled="isLoading || isRefreshing">
                <i class="fas" :class="isRefreshing ? 'fa-spinner fa-spin' : 'fa-search'"></i>
                {{ isRefreshing ? 'Filtrando...' : 'Aplicar filtros' }}
              </button>

              <button class="btn btn-outline-secondary" @click="clearFilters">
                <i class="fas fa-times me-1"></i>
                Limpiar filtros
              </button>
            </div>
          </div>
        </div>

        <div class="panel-refresh-strip" aria-hidden="true">
          <div v-if="isRefreshing && products.length && !isLoading" class="panel-refresh-spinner">
            <i class="fas fa-spinner fa-spin"></i>
          </div>
        </div>

        <ProductSelectionBar
          v-if="products.length"
          input-id="catalog-select-all"
          :selected-count="selectedCount"
          :total-count="products.length"
          @toggle-all="toggleAllVisible"
          @clear="clearSelection"
        >
          <template #actions>
            <button type="button" class="btn btn-sm btn-success" :disabled="bulkWorking" @click="requestBulkVisibility(true)">
              Mostrar
            </button>
            <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="bulkWorking" @click="requestBulkVisibility(false)">
              Ocultar
            </button>
            <button type="button" class="btn btn-sm btn-danger" :disabled="bulkWorking" @click="requestBulkDelete">
              Eliminar
            </button>
          </template>
        </ProductSelectionBar>

        <div v-if="isLoading" class="row g-4">
          <div v-for="n in 8" :key="n" class="col-lg-4">
            <div class="skeleton-loader">
              <div class="skeleton-text w-50"></div>
              <div class="skeleton-text w-75"></div>
              <div class="skeleton-text w-75"></div>
            </div>
          </div>
        </div>

        <div v-else-if="error && !products.length" class="alert alert-danger" role="alert">
          <i class="fas fa-exclamation-triangle me-2"></i>
          {{ error }}
          <button @click="fetchProducts(1, true, false)" class="btn btn-outline-danger btn-sm ms-3">
            <i class="fas fa-sync-alt me-1"></i>Reintentar
          </button>
        </div>

        <!-- Cards -->
        <div v-else-if="effectiveViewMode === 'cards'" class="row g-4" :class="{ 'panel-grid--refreshing': isRefreshing && products.length && !isLoading }">
          <div v-for="product in products" :key="product.id" class="col-md-6 col-lg-4">
            <div class="card product-card bg-dark text-light h-100" :class="{ 'product-card--selected': isSelected(product.id) }">
              <div class="card-body d-flex flex-column">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :checked="isSelected(product.id)"
                      @change="toggleSelection(product.id)"
                    />
                  </div>
                  <span v-if="productInStore(product.id)" class="badge bg-info text-dark">
                    <i class="fas fa-store me-1"></i>En tienda
                  </span>
                </div>

                <div class="d-flex align-items-start mb-3 gap-3">
                  <ProductImage :product="product" :alt="product.name" class="img-thumbnail flex-shrink-0" />
                  <div class="min-w-0">
                    <h5 class="mb-1 fs-6 fw-bold">{{ product.name }}</h5>
                    <p class="mb-1 text-muted small">{{ truncateText(product.description, 60) }}</p>
                    <small class="text-muted">{{ product.brand }}</small>
                  </div>
                </div>

                <div class="d-flex flex-wrap gap-1 mb-3">
                  <span class="badge bg-primary">{{ getTypeLabel(product.type) }}</span>
                  <button
                    class="badge border-0 p-0"
                    :class="isVisibleOnWeb(product) ? 'bg-success' : 'bg-danger'"
                    @click="toggleVisibility(product)"
                    :disabled="isTogglePending(product.id)"
                    style="cursor: pointer;"
                  >
                    <i class="fas" :class="isTogglePending(product.id) ? 'fa-spinner fa-spin' : (isVisibleOnWeb(product) ? 'fa-eye' : 'fa-eye-slash')"></i>
                    {{ isVisibleOnWeb(product) ? 'Visible' : 'Oculto' }}
                  </button>
                  <button
                    class="badge border-0 p-0"
                    :class="product.highlighted ? 'bg-warning text-dark' : 'bg-secondary'"
                    @click="toggleHighlighted(product)"
                    :disabled="isTogglePending(product.id)"
                    style="cursor: pointer;"
                  >
                    <i class="fas" :class="isTogglePending(product.id) ? 'fa-spinner fa-spin' : 'fa-star'"></i>
                    {{ product.highlighted ? 'Destacado' : 'Normal' }}
                  </button>
                  <button
                    class="badge border-0 p-0"
                    :class="product.limited_edition ? 'bg-danger' : 'bg-secondary'"
                    @click="toggleLimitedEdition(product)"
                    :disabled="isTogglePending(product.id)"
                    style="cursor: pointer;"
                  >
                    <i class="fas" :class="isTogglePending(product.id) ? 'fa-spinner fa-spin' : 'fa-fire'"></i>
                    {{ product.limited_edition ? 'Limitada' : 'Regular' }}
                  </button>
                </div>

                <div v-if="product.blisters?.length" class="text-muted small mb-3">
                  {{ product.blisters.length }} presentación{{ product.blisters.length === 1 ? '' : 'es' }}
                </div>

                <div class="d-flex flex-column gap-2 mt-auto">
                  <div class="d-flex gap-2">
                    <router-link
                      class="btn btn-secondary flex-grow-1"
                      :to="{ name: 'ProductEdit', params: { productId: product.id } }"
                    >
                      <i class="fas fa-edit me-1"></i>Editar
                    </router-link>
                    <button class="btn btn-danger" @click="confirmDelete(product)" title="Eliminar producto">
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                  <router-link
                    class="btn btn-outline-primary w-100"
                    :to="{ name: 'ProductDetails', params: { id: product.id } }"
                  >
                    <i class="fas fa-eye me-1"></i>Ver en la web
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- List -->
        <div v-else class="table-responsive panel-table-wrap" :class="{ 'panel-grid--refreshing': isRefreshing && products.length && !isLoading }">
          <table class="table table-dark table-hover align-middle mb-0">
            <thead>
              <tr>
                <th style="width: 40px;">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :checked="products.length && selectedCount === products.length"
                    :indeterminate="selectedCount > 0 && selectedCount < products.length"
                    @change="toggleAllVisible"
                  />
                </th>
                <th style="width: 72px;"></th>
                <th>Producto</th>
                <th>Tipo</th>
                <th>Estado</th>
                <th>Presentaciones</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="product in products" :key="product.id" :class="{ 'table-active': isSelected(product.id) }">
                <td>
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :checked="isSelected(product.id)"
                    @change="toggleSelection(product.id)"
                  />
                </td>
                <td>
                  <ProductImage :product="product" :alt="product.name" class="list-thumb" />
                </td>
                <td>
                  <div class="fw-medium">{{ product.name }}</div>
                  <small class="text-muted d-block">{{ truncateText(product.description, 50) }}</small>
                  <small class="text-muted">{{ product.brand }}</small>
                  <span v-if="productInStore(product.id)" class="badge bg-info text-dark ms-1">En tienda</span>
                </td>
                <td><span class="badge bg-primary">{{ getTypeLabel(product.type) }}</span></td>
                <td>
                  <div class="d-flex flex-wrap gap-1">
                    <span class="badge" :class="isVisibleOnWeb(product) ? 'bg-success' : 'bg-danger'">
                      {{ isVisibleOnWeb(product) ? 'Visible' : 'Oculto' }}
                    </span>
                    <span v-if="product.highlighted" class="badge bg-warning text-dark">Destacado</span>
                    <span v-if="product.limited_edition" class="badge bg-danger">Limitada</span>
                  </div>
                </td>
                <td>{{ product.blisters?.length || 0 }}</td>
                <td class="text-end text-nowrap">
                  <router-link
                    :to="{ name: 'ProductEdit', params: { productId: product.id } }"
                    class="btn btn-sm btn-outline-primary me-1"
                  >
                    <i class="fas fa-edit"></i>
                  </router-link>
                  <router-link
                    :to="{ name: 'ProductDetails', params: { id: product.id } }"
                    class="btn btn-sm btn-outline-light me-1"
                  >
                    <i class="fas fa-eye"></i>
                  </router-link>
                  <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(product)">
                    <i class="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="!products.length">
                <td colspan="7" class="text-center text-muted py-4">No se encontraron productos</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="!isLoading && hasNextPage" class="d-flex justify-content-center mt-4">
          <button class="btn btn-primary" @click="loadMoreProducts" :disabled="isLoading || isRefreshing">
            <i class="fas" :class="isRefreshing ? 'fa-spinner fa-spin' : 'fa-plus'"></i>
            {{ isRefreshing ? 'Cargando...' : 'Cargar más productos' }}
          </button>
        </div>

        <div v-if="!isLoading && products.length === 0 && !error" class="text-center py-5 text-muted">
          <i class="fas fa-seedling fa-3x mb-3"></i>
          <h4>No se encontraron productos</h4>
          <p>Probá con otros filtros o agregá un producto nuevo</p>
        </div>
      </template>

      <IngresoStoreTab
        v-else
        :view-mode="effectiveViewMode"
        @update:view-mode="viewMode = $event"
        @store-changed="loadStoreProductIds"
      />
    </div>

    <BulkActionConfirmModal
      :show="confirmModal.show"
      :title="confirmModal.title"
      :message="confirmModal.message"
      :detail="confirmModal.detail"
      :warning="confirmModal.warning"
      :confirm-label="confirmModal.confirmLabel"
      :confirm-class="confirmModal.confirmClass"
      :loading="confirmModal.loading"
      @confirm="runConfirm"
      @cancel="closeConfirm"
    />
  </section>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted, watch } from 'vue'
import ProductImage from '@/components/ProductImage.vue'
import { apiErrorMessage } from '@/utils/apiErrors'
import { useRoute, useRouter } from 'vue-router'
import {
  deleteProduct,
  getAllProductsForAdmin,
  setProductPublicWebVisibility,
  toggleProductHighlighted,
  toggleProductLimitedEdition,
  onProductsChanged,
} from '@/services/products'
import { getIngresoCatalog } from '@/services/ingreso'
import { useToast } from 'vue-toastification'
import { useItemSelection } from '@/composables/useItemSelection'
import { usePanelMobile } from '@/composables/usePanelMobile'
import IngresoStoreTab from '@/components/panel/IngresoStoreTab.vue'
import ProductViewToggle from '@/components/panel/ProductViewToggle.vue'
import ProductSelectionBar from '@/components/panel/ProductSelectionBar.vue'
import BulkActionConfirmModal from '@/components/panel/BulkActionConfirmModal.vue'

const VIEW_MODE_KEY = 'adminProductListViewMode'

const route = useRoute()
const router = useRouter()
const toast = useToast()

const activeTab = ref(route.query.tab === 'tienda' ? 'store' : 'catalog')
const viewMode = ref(localStorage.getItem(VIEW_MODE_KEY) || 'cards')
const { isMobile } = usePanelMobile()
const effectiveViewMode = computed(() => (isMobile.value ? 'cards' : viewMode.value))
const products = ref([])
const isLoading = ref(true)
const isRefreshing = ref(false)
const pendingToggles = ref(new Set())
const error = ref(null)
const productToDelete = ref(null)
const bulkWorking = ref(false)
const storeProductIds = ref(new Set())

const confirmModal = ref({
  show: false,
  title: '',
  message: '',
  detail: '',
  warning: '',
  confirmLabel: 'Confirmar',
  confirmClass: 'btn-primary',
  loading: false,
  handler: null,
})

const { selectedIds, selectedCount, isSelected, toggle, clear, toggleAll, pruneMissing } = useItemSelection()

const ITEMS_PER_PAGE = 9
const currentPage = ref(1)
const totalProductsCount = ref(0)
const hasNextPage = ref(false)
const hasActiveFilters = ref(false)
const filters = ref({
  search: '',
  type: '',
  availability: '',
  edition: '',
})

const totalProducts = computed(() => totalProductsCount.value || products.value.length)

watch(viewMode, (mode) => {
  localStorage.setItem(VIEW_MODE_KEY, mode)
})

watch(products, (list) => {
  pruneMissing(list.map((p) => p.id))
})

const setTab = (tab) => {
  activeTab.value = tab
  clear()
  router.replace({
    name: 'AdminProductList',
    query: tab === 'store' ? { tab: 'tienda' } : {},
  })
}

watch(
  () => route.query.tab,
  (tab) => {
    activeTab.value = tab === 'tienda' ? 'store' : 'catalog'
  }
)

const isVisibleOnWeb = (product) => product.show_on_public_web === true

const openConfirm = (config) => {
  confirmModal.value = {
    show: true,
    title: '',
    message: '',
    detail: '',
    warning: '',
    confirmLabel: 'Confirmar',
    confirmClass: 'btn-primary',
    loading: false,
    handler: null,
    ...config,
  }
}

const closeConfirm = () => {
  if (confirmModal.value.loading) return
  confirmModal.value.show = false
  productToDelete.value = null
}

const runConfirm = async () => {
  if (!confirmModal.value.handler) return
  confirmModal.value.loading = true
  try {
    await confirmModal.value.handler()
    confirmModal.value.show = false
    productToDelete.value = null
  } catch (err) {
    toast.error(err.message || 'Error al ejecutar la acción')
  } finally {
    confirmModal.value.loading = false
  }
}

const toggleSelection = (id) => toggle(id)
const clearSelection = () => clear()
const toggleAllVisible = () => toggleAll(products.value.map((p) => p.id))

const loadStoreProductIds = async () => {
  try {
    const data = await getIngresoCatalog({ page_size: 500 })
    const items = data.results || data
    const ids = new Set()
    items.filter((i) => i.source === 'catalog' && i.product_id).forEach((i) => ids.add(i.product_id))
    storeProductIds.value = ids
  } catch {
    storeProductIds.value = new Set()
  }
}

const productInStore = (productId) => storeProductIds.value.has(productId)

const loadMoreProducts = async () => {
  if (hasNextPage.value && !isLoading.value && !isRefreshing.value) {
    await fetchProducts(currentPage.value + 1, false, hasActiveFilters.value, { quiet: true })
  }
}

// Solo la request de productos más reciente puede actualizar la grilla.
let fetchSeq = 0

const fetchProducts = async (page = 1, resetProducts = true, useFilters = false, options = {}) => {
  const { quiet = false, ignoreCache = false } = options
  const seq = ++fetchSeq
  try {
    if (quiet) {
      isRefreshing.value = true
    } else {
      isLoading.value = true
    }
    error.value = null

    if (resetProducts && !quiet) {
      products.value = []
      currentPage.value = 1
      clear()
    }

    const filtersToSend = useFilters
      ? {
          search: filters.value.search,
          type: filters.value.type,
          availability: filters.value.availability,
          limitedEdition:
            filters.value.edition === 'limited' ? true : filters.value.edition === 'regular' ? false : null,
        }
      : {}

    const response = await getAllProductsForAdmin(ignoreCache, page, ITEMS_PER_PAGE, filtersToSend)

    if (seq !== fetchSeq) return false

    if (response?.results) {
      products.value = resetProducts ? response.results : [...products.value, ...response.results]
      totalProductsCount.value = response.count || response.results.length
      hasNextPage.value = !!response.next
      currentPage.value = page
      if (resetProducts) clear()
    } else if (Array.isArray(response)) {
      products.value = response
      totalProductsCount.value = response.length
      hasNextPage.value = false
    } else {
      products.value = []
      totalProductsCount.value = 0
      hasNextPage.value = false
    }
    return true
  } catch (err) {
    if (seq !== fetchSeq) return false
    const message = apiErrorMessage(err, 'No se pudieron cargar los productos.')
    // En modo silencioso no se tira la grilla actual: se avisa y se conserva lo mostrado.
    if (quiet && products.value.length) {
      toast.error(message)
    } else {
      error.value = message
      products.value = []
      totalProductsCount.value = 0
      hasNextPage.value = false
    }
    return false
  } finally {
    if (seq === fetchSeq) {
      isLoading.value = false
      isRefreshing.value = false
    }
  }
}

const forceRefresh = async (quiet = false) => {
  const ok = await fetchProducts(1, true, hasActiveFilters.value, { quiet, ignoreCache: true })
  if (ok) {
    await loadStoreProductIds()
    toast.success('Productos actualizados')
  }
}

const confirmDelete = (product) => {
  productToDelete.value = product
  openConfirm({
    title: 'Eliminar producto',
    message: `Se eliminará permanentemente "${product.name}" del catálogo.`,
    detail: 'Se borrará la ficha del producto y todas sus presentaciones asociadas.',
    warning: 'Esta acción no se puede deshacer.',
    confirmLabel: 'Eliminar',
    confirmClass: 'btn-danger',
    handler: executeSingleDelete,
  })
}

const requestBulkDelete = () => {
  const count = selectedCount.value
  if (!count) return
  openConfirm({
    title: 'Eliminar seleccionados',
    message: `Se eliminarán ${count} producto(s) del catálogo.`,
    detail: 'Se borrarán las fichas y presentaciones de cada producto seleccionado.',
    warning: 'Esta acción no se puede deshacer.',
    confirmLabel: 'Eliminar',
    confirmClass: 'btn-danger',
    handler: executeBulkDelete,
  })
}

const requestBulkVisibility = (visible) => {
  const count = selectedCount.value
  if (!count) return
  openConfirm({
    title: visible ? 'Mostrar en la web' : 'Ocultar de la web',
    message: visible
      ? `Se publicarán ${count} producto(s) en kameseeds.com.`
      : `Se ocultarán ${count} producto(s) del catálogo web.`,
    detail: visible
      ? 'Los usuarios podrán ver estos productos en la web pública.'
      : 'Los productos dejarán de mostrarse en la web, pero conservan su ficha en el panel.',
    confirmLabel: visible ? 'Mostrar' : 'Ocultar',
    confirmClass: visible ? 'btn-success' : 'btn-secondary',
    handler: () => executeBulkVisibility(visible),
  })
}

const executeSingleDelete = async () => {
  if (!productToDelete.value) return
  await deleteProduct(productToDelete.value.id)
  products.value = products.value.filter((p) => p.id !== productToDelete.value.id)
  toast.success(`"${productToDelete.value.name}" eliminado`)
}

const executeBulkDelete = async () => {
  bulkWorking.value = true
  try {
    const ids = [...selectedIds.value]
    for (const id of ids) {
      await deleteProduct(id)
    }
    products.value = products.value.filter((p) => !selectedIds.value.has(p.id))
    toast.success(`${ids.length} producto(s) eliminados`)
    clear()
  } finally {
    bulkWorking.value = false
  }
}

const executeBulkVisibility = async (visible) => {
  bulkWorking.value = true
  try {
    const ids = [...selectedIds.value]
    for (const id of ids) {
      await setProductPublicWebVisibility(id, visible)
    }
    products.value = products.value.map((p) =>
      selectedIds.value.has(p.id) ? { ...p, show_on_public_web: visible } : p
    )
    toast.success(`${ids.length} producto(s) ${visible ? 'visibles' : 'ocultos'} en la web`)
    clear()
  } finally {
    bulkWorking.value = false
  }
}

const truncateText = (text, length) => {
  if (!text) return ''
  if (text.length <= length) return text
  return text.substring(0, length) + '...'
}

const getTypeLabel = (type) =>
  ({
    auto: 'Autofloreciente',
    fem: 'Fotoperiódica',
  }[type] || type)

const FILTER_DEBOUNCE_MS = 350
let filterDebounce = null

const applyFilters = async () => {
  clearTimeout(filterDebounce)
  hasActiveFilters.value = Object.values(filters.value).some((value) => value && String(value).trim() !== '')
  currentPage.value = 1
  await fetchProducts(1, true, hasActiveFilters.value, { quiet: true })
}

const clearFilters = () => {
  filters.value = { search: '', type: '', availability: '', edition: '' }
  hasActiveFilters.value = false
  currentPage.value = 1
  // El watcher de filters dispara el fetch automáticamente.
}

// Auto-aplicar filtros al cambiar selects o escribir en el buscador (con debounce).
watch(
  filters,
  () => {
    clearTimeout(filterDebounce)
    filterDebounce = setTimeout(() => applyFilters(), FILTER_DEBOUNCE_MS)
  },
  { deep: true }
)

const isTogglePending = (id) => pendingToggles.value.has(id)

const runOptimisticToggle = async (product, field, nextValue, request, successMsg, errorMsg) => {
  if (pendingToggles.value.has(product.id)) return
  const idx = products.value.findIndex((p) => p.id === product.id)
  const prev = idx !== -1 ? products.value[idx] : product
  // Actualización optimista: el badge cambia al toque; el toast recién cuando resuelve.
  if (idx !== -1) products.value[idx] = { ...prev, [field]: nextValue }
  pendingToggles.value.add(product.id)
  try {
    await request()
    toast.success(successMsg)
  } catch (err) {
    // Revertir al estado anterior si falló la solicitud.
    if (idx !== -1) products.value[idx] = prev
    toast.error(err.message || errorMsg)
  } finally {
    pendingToggles.value.delete(product.id)
  }
}

const toggleVisibility = (product) => {
  const newVisible = !isVisibleOnWeb(product)
  runOptimisticToggle(
    product,
    'show_on_public_web',
    newVisible,
    () => setProductPublicWebVisibility(product.id, newVisible),
    `"${product.name}" ${newVisible ? 'visible' : 'oculto'} en la web`,
    'Error al cambiar la visibilidad'
  )
}

const toggleHighlighted = (product) => {
  const newHighlighted = !product.highlighted
  runOptimisticToggle(
    product,
    'highlighted',
    newHighlighted,
    () => toggleProductHighlighted(product.id, newHighlighted),
    `"${product.name}" ${newHighlighted ? 'destacado' : 'sin destacar'}`,
    'Error al cambiar el estado destacado'
  )
}

const toggleLimitedEdition = (product) => {
  const newLimitedEdition = !product.limited_edition
  runOptimisticToggle(
    product,
    'limited_edition',
    newLimitedEdition,
    () => toggleProductLimitedEdition(product.id, newLimitedEdition),
    `"${product.name}" ${newLimitedEdition ? 'edición limitada' : 'edición regular'}`,
    'Error al cambiar la edición'
  )
}

// Al regresar de otra pestaña se recargan datos, sin pisar una edición activa.
const refreshVisibleCatalog = () => {
  if (document.visibilityState === 'visible' && activeTab.value === 'catalog' &&
      !isLoading.value && !isRefreshing.value && !selectedCount.value && !pendingToggles.value.size) {
    fetchProducts(1, true, hasActiveFilters.value, { quiet: true })
  }
}
let refreshInterval
let stopProductChanges
onMounted(() => {
  forceRefresh()
  stopProductChanges = onProductsChanged(() => {
    if (activeTab.value === 'catalog' && !selectedCount.value && !pendingToggles.value.size) {
      fetchProducts(1, true, hasActiveFilters.value, { quiet: true })
    }
  })
  document.addEventListener('visibilitychange', refreshVisibleCatalog)
  refreshInterval = setInterval(refreshVisibleCatalog, 5 * 60 * 1000)
})
onUnmounted(() => {
  stopProductChanges?.()
  fetchSeq += 1
  clearInterval(refreshInterval)
  clearTimeout(filterDebounce)
  document.removeEventListener('visibilitychange', refreshVisibleCatalog)
})
</script>

<style scoped>
section {
  min-height: 60vh;
}

.product-tabs {
  border-bottom: 1px solid var(--panel-border);
}

.product-tabs .nav-link {
  color: rgba(255, 255, 255, 0.65);
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  padding: 0.75rem 1.25rem;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;
}

.product-tabs .nav-link:hover {
  color: var(--white-color);
  border-bottom-color: rgba(var(--bs-primary-rgb), 0.4);
}

.product-tabs .nav-link.active {
  color: var(--primary-color);
  border-bottom-color: var(--primary-color);
  background: transparent;
}

.card,
.product-card {
  border: 1px solid var(--panel-border);
  border-radius: 0.5rem;
  background: var(--panel-surface);
  box-shadow: none;
  transition: transform 0.3s ease-in-out, border-color 0.2s ease;
}

.card:hover,
.product-card:hover {
  transform: translateY(-0.25rem);
}

.product-card--selected {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 1px rgba(var(--bs-primary-rgb), 0.35);
}

.img-thumbnail {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 0.25rem;
}

.img-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.35);
  font-size: 1.75rem;
}

.list-thumb {
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 0.35rem;
}

.list-thumb--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.35);
  font-size: 1.25rem;
}

.badge {
  font-size: 0.85rem;
  white-space: nowrap;
  padding: 0.4rem 0.65rem;
  font-weight: 500;
}

button.badge.border-0.p-0 {
  padding: 0.35rem 0.6rem !important;
  font-size: 0.8rem;
  margin-right: 0.4rem;
}

button.badge:disabled {
  opacity: 0.6;
  cursor: progress !important;
}

/* Indicador flotante de refresh silencioso (no desplaza la grilla) */
.panel-refresh-strip {
  position: relative;
  height: 0;
  z-index: 5;
}

.panel-refresh-spinner {
  position: absolute;
  top: -14px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--primary-color, #7f2bc3);
  font-size: 0.85rem;
  background: rgba(0, 0, 0, 0.55);
  padding: 0.25rem 0.7rem;
  border-radius: 999px;
  border: 1px solid var(--panel-border);
  backdrop-filter: blur(4px);
}

.panel-grid--refreshing {
  opacity: 0.55;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

.skeleton-loader {
  background: #1a1a1a;
  padding: 1.5rem;
  border-radius: 0.5rem;
}

.skeleton-text {
  height: 20px;
  margin-bottom: 10px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.form-select {
  background-color: rgba(0, 0, 0, 0.25);
  color: var(--white-color);
  border: 1px solid var(--panel-border);
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 38px;
  line-height: 1.5;
}

.form-select:hover,
.form-select:focus {
  border-color: var(--primary-color);
}

.input-group .form-control {
  border: 1px solid var(--dark-color);
  color: var(--white-color-2);
  font-size: 0.9rem;
  height: 38px;
}

.input-group-text {
  background-color: var(--black-color-3);
  border: 1px solid var(--dark-color);
  color: var(--white-color-2);
}

.btn-outline-secondary {
  background-color: transparent;
  color: var(--white-color-2);
  border: 1px solid var(--black-color-2);
  height: 38px;
}

@media (max-width: 768px) {
  .d-flex.flex-wrap.gap-3 {
    flex-direction: column;
    width: 100%;
    gap: 0.75rem !important;
  }

  .form-select,
  .btn-outline-secondary,
  .input-group,
  .flex-grow-1 {
    width: 100% !important;
    max-width: 100% !important;
  }

  .flex-grow-1 {
    flex-grow: 0 !important;
  }
}
</style>
